// MeusDados.js — Seção "Dados Pessoais" (redesenhada).
// Três cartões: Dados Pessoais, Redes Sociais e Preferências.
// A foto de perfil é enviada ao backend (upload-image-usuario-editora) e o
// caminho é salvo na coluna `image`. Os campos de texto persistem via
// useProfileData().updateProfile (PUT dados-usuario-editora).
//
// OS COINS APARECEM NA LINHA DO CAMPO. Telefone, CPF, Curso/Área de Atuação e a
// foto rendem Insigne Coins, e o quanto cada um ainda rende é desenhado à
// direita do próprio rótulo ("+10"). Quem diz o número é o servidor
// (useCoinsPerfil): a tela não pode concluir "está preenchido, logo já paguei",
// porque o crédito tem trava no banco e também paga dados que chegaram por
// outro caminho — o CPF do checkout, o telefone do cadastro. Depois de salvar,
// os valores são relidos: é o sumiço do "+10" que avisa que a moeda entrou.
import React, { useEffect, useRef, useState } from "react";
import useProfileData from "../../../hooks/userProfileData";
import useCoinsPerfil from "../../../hooks/useCoinsPerfil";
import { SectionCard, Field, TextInput, TextArea, PrimaryButton, Toast, useToast } from "../ui/ProfileUI";
// Direto do arquivo compartilhado: o ProfileUI só reexporta apelidos de campo,
// e a marca de bônus não pertence só ao perfil.
import { BonusCoins } from "../../ui/Formulario";
import RegrasCoinsPerfil from "./CoinsDoPerfil";

const API_URL = process.env.REACT_APP_API;

// Máscara de CPF. Fica aqui e não numa lib: são onze dígitos e um formato fixo,
// e a única regra que importa é GUARDAR SÓ DÍGITO. O backend conta o campo como
// preenchido com `trim(...) !== ''`, então "000.000.000-00" e "00000000000"
// valeriam igual — mas o CPF também vai para o checkout, e ali a pontuação
// atrapalha. Grava limpo, mostra bonito.
const soDigitos = (v) => String(v || "").replace(/\D/g, "").slice(0, 11);
const mascaraCpf = (v) => {
  const d = soDigitos(v);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const resolveFoto = (data) => {
  const f = data?.image || data?.foto || data?.imagem || data?.avatar || null;
  if (!f) return null;
  return /^(https?:|data:|blob:)/i.test(f) ? f : `${API_URL}public/${f}`;
};

/**
 * `secao` decide QUAIS cartões desta tela aparecem — "perfil" (dados pessoais),
 * "redes" (redes sociais e preferências) ou "todos".
 *
 * A divisão existe porque os três cartões juntos passavam de duas telas de
 * rolagem, e desde 11/09/2026 eles são sub-abas de "Dados Pessoais" (ver
 * dadosPessoais/DadosPessoais.js). O FORMULÁRIO CONTINUA SENDO UM SÓ: o estado,
 * o carregamento e o `salvar` são os mesmos, e cada cartão salva apenas os
 * campos dele — separar em dois componentes duplicaria a busca do perfil e
 * faria a tela pedir os mesmos dados duas vezes ao trocar de sub-aba.
 */
const MeusDados = ({ secao = "todos" }) => {
  const mostrarPerfil = secao === "todos" || secao === "perfil";
  const mostrarRedes = secao === "todos" || secao === "redes";
  const { profileData, loading, updateProfile, fetchProfileData } = useProfileData();
  const { toast, fireToast } = useToast();
  const { premioDoCampo, fotoImportada, aGanhar, recarregar: recarregarCoins } = useCoinsPerfil();

  const [form, setForm] = useState({
    nome: "", biografia: "", email: "", telefone: "",
    site: "", facebook: "", instagram: "", lattes: "",
    profissao: "", receber_novidades: true, cpf: "",
  });
  const [fotoPreview, setFotoPreview] = useState(null);
  const [picMenu, setPicMenu] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [saving, setSaving] = useState("");
  const fileRef = useRef(null);
  const picRef = useRef(null);

  useEffect(() => {
    if (!profileData) return;
    setForm((f) => ({
      ...f,
      nome: profileData.nome || "",
      biografia: profileData.biografia || profileData.bio || "",
      email: profileData.email || "",
      telefone: profileData.telefone || "",
      cpf: mascaraCpf(profileData.cpf || ""),
      site: profileData.site || profileData.website || "",
      facebook: profileData.facebook || "",
      instagram: profileData.instagram || "",
      lattes: profileData.lattes || "",
      profissao: profileData.profissao || profileData.curso || "",
      receber_novidades: profileData.receber_novidades ?? true,
    }));
  }, [profileData]);

  // O menu da foto fecha no clique fora e no Esc.
  //
  // Não fechava por nenhum dos dois: o único jeito de se livrar dele era clicar
  // de novo no lápis. Quem abria por engano e ia digitar o telefone continuava
  // com a caixinha aberta por cima do formulário. É o mesmo par de ouvintes da
  // etiqueta da estante (meusLivros/EtiquetaDoLivro.js) — popover deste site
  // fecha assim.
  //
  // `mousedown`, e não `click`: no `click` o ouvinte dispararia depois de o
  // botão de dentro do menu já ter rodado, e o item "Trocar foto" fecharia a
  // caixa antes de abrir o seletor de arquivo em parte dos navegadores.
  useEffect(() => {
    if (!picMenu) return;
    const aoClicar = (e) => {
      if (picRef.current && !picRef.current.contains(e.target)) setPicMenu(false);
    };
    const aoTeclar = (e) => { if (e.key === "Escape") setPicMenu(false); };
    document.addEventListener("mousedown", aoClicar);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [picMenu]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const salvar = async (campos, label) => {
    if (!updateProfile) { fireToast("Atualização indisponível.", "error"); return; }
    try {
      setSaving(label);
      const payload = {};
      campos.forEach((c) => (payload[c] = c === "cpf" ? soDigitos(form[c]) : form[c]));
      // CPF vazio não vai: mandar "" apagaria um CPF já cadastrado pelo
      // checkout só porque a pessoa salvou o nome sem tocar neste campo.
      if ("cpf" in payload && !payload.cpf) delete payload.cpf;
      await updateProfile(payload);
      // O PUT já creditou o que estava pendente. Reler é o que risca o "+10"
      // na hora; sem isto o recibo só apareceria na próxima visita, e aí
      // ninguém liga mais o coin ao campo que acabou de preencher.
      recarregarCoins();
      fireToast(`${label} atualizado(a) com sucesso!`);
    } catch (e) {
      // A MENSAGEM DO SERVIDOR, quando existe: `updateProfile` já traz o
      // primeiro erro de validação (ex.: "Informe o seu nome."). A frase
      // genérica escondia até um 422 que dizia exatamente o que faltava.
      fireToast(e?.message || "Não foi possível salvar. Tente novamente.", "error");
    } finally {
      setSaving("");
    }
  };

  // Envia a foto ao backend e salva o caminho na coluna `image`
  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    setPicMenu(false);
    if (!file) return;
    setFotoPreview(URL.createObjectURL(file)); // preview imediato
    try {
      setUploadingFoto(true);
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${API_URL}api/usuario-editora/upload-image-usuario-editora`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) throw new Error("upload falhou");
      const data = await r.json();
      setFotoPreview(`${API_URL}public/${data.image}`);
      if (fetchProfileData) await fetchProfileData();
      // A foto é o item mais caro do perfil: o "+25" precisa sumir junto com o
      // envio, ou o envio parece não ter valido nada.
      recarregarCoins();
      fireToast("Foto de perfil atualizada!");
    } catch {
      fireToast("Não foi possível enviar a foto agora (mostrando prévia local).", "error");
    } finally {
      setUploadingFoto(false);
    }
  };

  const removerFoto = async () => {
    setPicMenu(false);
    setFotoPreview(null);
    if (updateProfile) { try { await updateProfile({ image: "" }); } catch {} }
  };

  const foto = fotoPreview || resolveFoto(profileData);
  const premioFoto = premioDoCampo("foto");

  if (loading) {
    return <SectionCard title="Dados Pessoais"><p className="text-white/60">Carregando dados do perfil...</p></SectionCard>;
  }

  // ===== Cabeçalho do cartão: a foto, o nome e o e-mail =====
  //
  // A foto MORAVA NO MEIO DA COLUNA DIREITA, entre o e-mail e o telefone, com um
  // rótulo "Foto de Perfil" por cima — desenhada como se fosse mais um campo a
  // preencher. Não é: ela é o que diz DE QUEM é esta página. Subiu para o topo,
  // ao lado do nome e do e-mail, e o resto do cartão virou só formulário. Quem
  // abre a aba agora reconhece a própria conta antes de ler qualquer rótulo.
  //
  // Nome e e-mail saem de `form`, e não de `profileData`, de propósito: são os
  // mesmos campos que estão logo abaixo, e o cabeçalho tem que acompanhar o que
  // a pessoa está digitando. Um cabeçalho que só mudasse depois do "Atualizar"
  // pareceria não ter registrado a edição.
  //
  // SOBRE OS ESPAÇOS DAQUI PARA BAIXO: o Bootstrap declara `gap-0..5`,
  // `p*-0..5` e `m*-0..5` com !important (ver "CONFLITO COM BOOTSTRAP" no
  // App.css), então `gap-4` valeria 24px e `pt-5` valeria 48px — não o que o
  // nome diz. Por isso esta tela usa 6 e 8 (que o Bootstrap não alcança), o
  // fracionário (`gap-3.5`, `py-2.5`) ou a variante com breakpoint
  // (`sm:gap-6`), que vira outro nome de classe.
  //
  // O CENTRO É SÓ DO CELULAR, e por isso `max-sm:text-center` em vez do par
  // `text-center sm:text-left`: `.text-center` também é classe do Bootstrap,
  // com !important, e anulava o `sm:text-left`. No desktop o nome e o e-mail
  // ficavam centralizados no meio do cartão, longe da foto (visto pelo dono em
  // 11/09/2026). A variante `max-sm:` é outro nome de classe, que o Bootstrap
  // não tem.
  const CabecalhoPerfil = (
    <div className="flex flex-col items-center gap-3.5 border-b border-line pb-6 max-sm:text-center
                    sm:flex-row sm:items-center sm:gap-6">
      {/* O `ref` fica no envelope da foto (e não só no menu) porque é ele que
          delimita o "dentro" para o clique fora: o lápis que abre a caixinha
          precisa contar como dentro, senão o mesmo clique abriria e fecharia. */}
      <div ref={picRef} className="relative shrink-0">
        {/* `bg-insigne-navy-deep` e não `navy-soft`: navy-soft é o fundo do
            próprio `.bloco`, e sem foto o círculo sumia dentro do cartão. */}
        <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-line bg-insigne-navy-deep flex items-center justify-center">
          {foto ? (
            <img src={foto} alt="Foto de perfil" className="w-full h-full object-cover" />
          ) : (
            <i className="bi bi-person text-5xl text-white/40" />
          )}
          {uploadingFoto && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-line border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setPicMenu((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={picMenu}
          aria-label="Opções da foto de perfil"
          className="foco-laranja absolute bottom-0 right-0 w-9 h-9 rounded-full bg-insigne-orange text-paper
                     flex items-center justify-center shadow-lg hover:bg-insigne-orange-dark transition-colors"
        >
          <i className="bi bi-pencil-fill text-sm" />
        </button>
        {/* ERAM TRÊS OPÇÕES, e duas delas rodavam a MESMA linha: "Enviar
            Imagem" e "Editar Imagem" chamavam as duas `fileRef.click()`. Não
            existe editor de imagem neste site — a única edição possível é
            mandar outro arquivo. Duas opções para uma ação só não oferecem
            escolha, só fazem a pessoa procurar a diferença entre elas.

            No celular a caixinha nasce centrada sob o avatar (que ali está
            centrado); a partir de `sm` ela alinha pela esquerda, com o avatar.
            `left-0` fixo estourava a borda direita do cartão em 400px.

            A sombra é `shadow-xl`, e não `shadow-lg`: `.shadow-lg` também é
            classe do Bootstrap, com !important, e trocaria esta sombra por um
            borrão de 3rem (ver "CONFLITO COM BOOTSTRAP" no App.css). */}
        {picMenu && (
          <div
            role="menu"
            className="absolute z-20 top-full mt-2 left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0
                       w-48 overflow-hidden rounded-controle border border-line bg-insigne-navy-deep text-left shadow-xl"
          >
            <button type="button" role="menuitem" onClick={() => fileRef.current?.click()} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm text-white/90 hover:bg-white/5">
              <i className="bi bi-upload" /> Trocar foto
            </button>
            <button type="button" role="menuitem" onClick={removerFoto} className="flex w-full items-center gap-2.5 border-t border-line px-3.5 py-2.5 text-sm text-red-400 hover:bg-white/5">
              <i className="bi bi-trash" /> Remover foto
            </button>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif" className="hidden" onChange={onPickFile} />
      </div>

      <div className="min-w-0 flex-1">
        <h3 className="text-lg md:text-xl font-bold text-white break-words">
          {form.nome || "Complete o seu nome"}
        </h3>
        <p className="text-sm text-white/60 break-all">{form.email || "Sem e-mail cadastrado"}</p>

        {/* A foto não é um `Campo`, então o bônus vem colado ao rótulo dela em
            vez de na borda direita da coluna: é esta palavra ("Foto de perfil")
            que diz de qual dado o "+25" fala. Solto ao lado do NOME, ele
            prometeria pagar pelo nome. */}
        <div className="mt-2.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 sm:justify-start">
          <span className="text-sm font-medium text-white/80">Foto de perfil</span>
          <BonusCoins
            coins={premioFoto?.coins}
            recebido={premioFoto?.recebido}
            title={`Enviar sua foto rende ${premioFoto?.coins} Insigne Coins, uma vez só. Vale mais que os outros dados porque é o único que exige sair do formulário e achar um arquivo.`}
          />
          <span className="text-xs text-white/40">Tamanho máximo 20mb • Formato PNG, JPG, GIF</span>
        </div>

        {/* A pessoa vê o próprio rosto no avatar (importado do Lattes ou do
            WhatsApp por um comando da editora) e, ao lado, um "+25" dizendo que
            a foto ainda rende. Sem esta linha a contradição fica sem explicação
            — e é o item mais caro da tela. Pagar pela importada não resolveria:
            essas colunas são preenchidas pelo admin, não por quem usa o site. */}
        {fotoImportada && premioFoto && !premioFoto.recebido && (
          <p className="mt-2 text-xs text-white/50 leading-relaxed">
            A foto que aparece foi importada pela editora. Envie uma sua para ganhar os coins.
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-8">
      <Toast {...toast} />

      {/* ===== Dados Pessoais ===== */}
      {mostrarPerfil && (
      <SectionCard title="Dados Pessoais">
        {CabecalhoPerfil}

        {/* AS DUAS LEGENDAS existem porque a diferença entre as colunas não é
            de assunto, é de EXPOSIÇÃO: o que está à esquerda outra pessoa lê no
            site, o que está à direita só a editora vê. A dica de cada campo já
            dizia isso um por um ("não será exibido publicamente", três vezes);
            a legenda diz de uma vez e dá à coluna um motivo para existir. */}
        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Coluna esquerda: o que aparece para os outros */}
          <div className="flex flex-col gap-6">
            <p className="-mb-1 text-xs font-bold uppercase tracking-wider text-white/45">Perfil público</p>
            <Field label="Nome de Usuário" hint="Seu nome que será visto por outros usuários do site, em avaliações e etc.">
              <TextInput value={form.nome} onChange={set("nome")} placeholder="Insira o seu nome completo..." />
            </Field>
            <Field label="Bio" hint="Esse texto poderá ser visualizado publicamente por outros usuários da plataforma que visualizarem o seu perfil." className="flex-1" premio={premioDoCampo("biografia")}>
              <TextArea value={form.biografia} onChange={set("biografia")} rows={8} placeholder="Insira sua Bio..." className="h-full" />
            </Field>
          </div>

          {/* Coluna direita: o que só a editora vê */}
          <div className="flex flex-col gap-6">
            <p className="-mb-1 text-xs font-bold uppercase tracking-wider text-white/45">Contato e cobrança</p>
            <Field label="E-mail" hint="Usado para login e comunicações da plataforma.">
              <TextInput type="email" value={form.email} onChange={set("email")} placeholder="seu@email.com" />
            </Field>
            <Field label="Telefone" hint="Não será exibido publicamente." premio={premioDoCampo("telefone")}>
              <TextInput value={form.telefone} onChange={set("telefone")} placeholder="(00) 00000-0000" />
            </Field>
            {/* O CPF valia 10 Insigne Coins e não tinha onde ser digitado: o
                único caminho era o checkout. Quem nunca comprou via o item na
                lista de recompensas sem nenhuma forma de completá-lo. A lista
                acabou; o preço agora está no campo, que é onde ele se paga. */}
            <Field label="CPF" hint="Usado na emissão da compra. Não é exibido publicamente." premio={premioDoCampo("cpf")}>
              <TextInput
                value={form.cpf}
                onChange={(e) => setForm((f) => ({ ...f, cpf: mascaraCpf(e.target.value) }))}
                placeholder="000.000.000-00"
                inputMode="numeric"
              />
            </Field>
          </div>
        </div>

        {/* O botão ficava solto logo depois do último campo, e lido de relance
            parecia pertencer só a ele. Com o traço em cima ele vira o RODAPÉ do
            cartão — o fim de um assunto —, que é o que ele é: salva a seção
            inteira, não o campo acima. Vale para os três cartões desta tela. */}
        <div className="mt-8 flex flex-wrap items-center gap-2.5 border-t border-line pt-6">
          <PrimaryButton onClick={() => salvar(["nome", "biografia", "email", "telefone", "cpf"], "Perfil")} disabled={saving === "Perfil"}>
            {saving === "Perfil" ? "Atualizando..." : "Atualizar Perfil"}
          </PrimaryButton>
        </div>
      </SectionCard>
      )}

      {/* ===== Redes Sociais ===== */}
      {mostrarRedes && (
      <SectionCard title="Redes Sociais">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Field label="Site"><TextInput value={form.site} onChange={set("site")} placeholder="Insira o endereço da web..." /></Field>
          <Field label="Facebook"><TextInput value={form.facebook} onChange={set("facebook")} placeholder="Insira o endereço da web..." /></Field>
          <Field label="Instagram"><TextInput value={form.instagram} onChange={set("instagram")} placeholder="Insira o endereço da web..." /></Field>
          {/* O Lattes só paga com um link do próprio Lattes (lattes.cnpq.br ou
              buscatextual.cnpq.br): qualquer texto no campo pagaria como um
              currículo de verdade. A frase do selo diz isso antes de a pessoa
              colar outro endereço e achar que o site esqueceu dela. */}
          <Field
            label="Lattes"
            premio={
              premioDoCampo("lattes") && {
                ...premioDoCampo("lattes"),
                titulo: `O link do seu currículo Lattes (lattes.cnpq.br) rende ${premioDoCampo("lattes").coins} Insigne Coins, uma vez só.`,
              }
            }
          >
            <TextInput value={form.lattes} onChange={set("lattes")} placeholder="http://lattes.cnpq.br/..." />
          </Field>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-2.5 border-t border-line pt-6">
          <PrimaryButton onClick={() => salvar(["site", "facebook", "instagram", "lattes"], "Redes Sociais")} disabled={saving === "Redes Sociais"}>
            {saving === "Redes Sociais" ? "Atualizando..." : "Atualizar Redes Sociais"}
          </PrimaryButton>
        </div>
      </SectionCard>
      )}

      {/* ===== Preferências ===== */}
      {mostrarRedes && (
      <SectionCard title="Preferências">
        {/* "Curso/Área de Atuação" é a coluna `profissao` no servidor — é de lá
            que sai o bônus, e não de um item com este nome. */}
        <Field label="Curso/Área de Atuação" className="max-w-xl" premio={premioDoCampo("profissao")}>
          <TextInput value={form.profissao} onChange={set("profissao")} placeholder="Insira aqui seu curso ou área de atuação..." />
        </Field>

        {/* O QUADRO DO CHECKBOX é o mesmo controle dos campos acima, e não uma
            caixa inventada: `rounded-controle`, `border border-line` e o fundo
            `navy-deep` de `campoCls`. Antes era um `rounded-xl` com
            `bg-white/[0.03]` escritos à mão — uma superfície que não existia em
            nenhum outro lugar do site, flutuando entre campos que tinham outra.

            `.campo-foco` é o anel laranja de quem DIGITA (App.css). Aqui ele
            entra por `:focus-within`: o alvo de teclado é o quadradinho de
            16px, e sem isto quem chega de Tab não enxerga onde parou.

            A frase com o e-mail fica: um "receber novidades" sem endereço não
            diz PARA ONDE elas vão, e o e-mail mostrado é o do campo ao lado,
            que pode estar sendo editado neste instante. */}
        <label className="campo-foco mt-6 flex max-w-xl cursor-pointer items-start gap-2.5 rounded-controle
                          border border-line bg-insigne-navy-deep p-3.5 transition-shadow">
          <input
            type="checkbox"
            checked={!!form.receber_novidades}
            onChange={(e) => setForm((f) => ({ ...f, receber_novidades: e.target.checked }))}
            className="mt-0.5 h-4 w-4 shrink-0 accent-insigne-orange"
          />
          <span className="text-sm">
            <span className="font-medium text-white">Receber informações e novidades</span>
            <span className="mt-0.5 block text-white/55">
              Você receberá as atualizações no seu e-mail cadastrado: <strong className="break-all text-white/80">{form.email || "seu@email.com"}</strong>
            </span>
          </span>
        </label>

        <div className="mt-8 flex flex-wrap items-center gap-2.5 border-t border-line pt-6">
          <PrimaryButton onClick={() => salvar(["profissao", "receber_novidades"], "Preferências")} disabled={saving === "Preferências"}>
            {saving === "Preferências" ? "Atualizando..." : "Atualizar Preferências"}
          </PrimaryButton>
        </div>
      </SectionCard>
      )}

      {/* As duas regras que não cabem num "+10": paga uma vez por dado, e o
          endereço conta como um dado só. Ficam no fim porque são letra miúda —
          quem quiser entender desce até elas; quem não quiser não é
          interrompido por um painel antes do formulário. */}
      <RegrasCoinsPerfil aGanhar={aGanhar} />
    </div>
  );
};

export default MeusDados;
